import React from 'react';
import InfoOutline from 'material-ui/svg-icons/action/info-outline';
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
} from 'material-ui/Table';
import {
    colorForPercent,
    formatByKind,
    formatPercent,
    percentChange,
} from './metrics';
import { HoverTip, LetterLink } from './HoverTip';

const COMPARE_HEADERS = [
    {
        label: 'Mail',
        title: 'Mail — corpus document included in the run',
    },
    {
        label: 'Type',
        title: 'Type — recognition model this document was run with',
    },
    {
        label: 'Presence',
        title: 'Presence — whether the document is in both runs, only the baseline, or only the candidate',
    },
    {
        label: 'Matched',
        title: 'Matched annotations — how many annotations the pipeline matched',
    },
    {
        label: 'Annotations',
        title: 'Annotations created — how many annotations recognition created',
    },
    {
        label: 'Time',
        title: 'Recognition time — time spent recognizing the document, in seconds',
    },
    {
        label: 'Heap',
        title: 'Heap after GC — heap used after the job garbage-collected',
    },
    {
        label: 'newclarified',
        title: 'New clarified — newly clarified fields from recognition output',
    },
];

const METRIC_COLS = [
    { key: 'totalMatchedAnnotations', kind: 'count' },
    { key: 'annotationsCreated', kind: 'count' },
    { key: 'timeSpent', kind: 'time' },
    { key: 'heapUsedAfterJobGcBytes', kind: 'bytes' },
];

function canonicalize(value) {
    if (Array.isArray(value)) {
        return value.map(canonicalize);
    }
    if (value && typeof value === 'object') {
        const out = {};
        Object.keys(value).sort().forEach((key) => {
            out[key] = canonicalize(value[key]);
        });
        return out;
    }
    return value;
}

function shapeDiff(left, right) {
    if (Object.is(left, right)) {
        return 'equal';
    }
    const leftList = Array.isArray(left);
    const rightList = Array.isArray(right);
    if (leftList || rightList) {
        if (!leftList || !rightList || left.length !== right.length) {
            return 'structure';
        }
        let valuesDiffer = false;
        for (let index = 0; index < left.length; index += 1) {
            const child = shapeDiff(left[index], right[index]);
            if (child === 'structure') {
                return 'structure';
            }
            if (child === 'value') {
                valuesDiffer = true;
            }
        }
        return valuesDiffer ? 'value' : 'equal';
    }
    const leftObject = left !== null && typeof left === 'object';
    const rightObject = right !== null && typeof right === 'object';
    if (leftObject || rightObject) {
        if (!leftObject || !rightObject) {
            return 'structure';
        }
        const leftKeys = Object.keys(left);
        const rightKeys = Object.keys(right);
        if (leftKeys.length !== rightKeys.length || leftKeys.some(key => !Object.prototype.hasOwnProperty.call(right, key))) {
            return 'structure';
        }
        let valuesDiffer = false;
        for (let index = 0; index < leftKeys.length; index += 1) {
            const child = shapeDiff(left[leftKeys[index]], right[leftKeys[index]]);
            if (child === 'structure') {
                return 'structure';
            }
            if (child === 'value') {
                valuesDiffer = true;
            }
        }
        return valuesDiffer ? 'value' : 'equal';
    }
    if (typeof left !== typeof right) {
        return 'structure';
    }
    return left === right ? 'equal' : 'value';
}

function compareResults(left, right) {
    const diff = shapeDiff(canonicalize(left), canonicalize(right));
    if (diff === 'equal') {
        return 'match';
    }
    if (diff === 'value') {
        return 'partial';
    }
    return 'mismatch';
}

function matchScore(rows) {
    const shared = (rows || []).filter(row => row.presence === 'both');
    const matched = shared.reduce((sum, row) => (
        sum + (compareResults(row.fromResult, row.toResult) === 'match' ? 1 : 0)
    ), 0);
    return { matched, total: shared.length };
}

function formatMatch(score) {
    if (!score.total) {
        return '—';
    }
    const pct = (score.matched / score.total) * 100;
    const digits = Number.isInteger(pct) ? 0 : 1;
    return `${pct.toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 })}%`;
}

function sharedSide(rows, side) {
    const out = {};
    METRIC_COLS.forEach(({ key }) => {
        let total = 0;
        let seen = false;
        (rows || []).forEach((row) => {
            if (row.presence !== 'both') {
                return;
            }
            const value = row[side] && row[side][key];
            if (typeof value === 'number') {
                total += value;
                seen = true;
            }
        });
        if (seen) {
            out[key] = total;
        }
    });
    return out;
}

function metricCells({ fromMetrics, toMetrics, comparable, percentMode }) {
    const cells = METRIC_COLS.map(({ key, kind }) => {
        const from = fromMetrics && fromMetrics[key];
        const to = toMetrics && toMetrics[key];
        const pct = comparable ? percentChange(from, to) : undefined;
        const lowerIsBetter = kind === 'time' || kind === 'bytes';
        const color = comparable ? colorForPercent(pct, lowerIsBetter) : undefined;
        const rawValue = comparable && typeof from === 'number' && typeof to === 'number'
            ? to - from
            : (to !== undefined ? to : from);
        const rawText = formatByKind(rawValue, kind, comparable);
        const pctText = formatPercent(pct);
        const shown = percentMode && comparable ? pctText : rawText;
        const tip = comparable ? (percentMode ? rawText : pctText) : undefined;
        return (
            <TableRowColumn key={key}>
                <HoverTip label={tip}>
                    <span style={color ? { color } : undefined}>{shown}</span>
                </HoverTip>
            </TableRowColumn>
        );
    });
    cells.push(<TableRowColumn key="nc">—</TableRowColumn>);
    return cells;
}

export default function CompareResults({ data, percentMode, onOpenDoc }) {
    const sharedFrom = sharedSide(data.rows, 'from');
    const sharedTo = sharedSide(data.rows, 'to');
    const score = matchScore(data.rows);

    return (
        <div>
            <p className="test-runs-match">
                <HoverTip label="Share of documents in both runs whose recognition result is identical. A partial match keeps the same structure with different values and still scores 0. A different structure scores 0.">
                    <span>Match {formatMatch(score)}</span>
                </HoverTip>
                <span className="test-runs-match-detail">
                    {score.total ? `${score.matched} of ${score.total} shared documents` : 'No documents in both runs'}
                </span>
            </p>
            <p className="test-runs-hint">
                <InfoOutline
                    color="#9e9e9e"
                    style={{ width: 18, height: 18, flex: '0 0 auto' }}
                />
                <span>
                    Deltas are shown only for documents in both runs. Added/removed rows keep
                    their own values. Footer totals use shared documents only. Unlinked rows
                    are documents no longer in the live corpus mapping.
                </span>
            </p>
            <Table selectable={false}>
                <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                    <TableRow>
                        {COMPARE_HEADERS.map((header) => (
                            <TableHeaderColumn key={header.label} className="test-runs-compare-header">
                                <HoverTip label={header.title}>
                                    <span>{header.label}</span>
                                </HoverTip>
                            </TableHeaderColumn>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false} showRowHover>
                    {(data.rows || []).map((row) => {
                        const tone = row.presence === 'both' ? compareResults(row.fromResult, row.toResult) : '';
                        return (
                            <TableRow
                                key={row.docId}
                                className={`test-runs-doc-row presence-${row.presence}${row.linked === false ? ' presence-unlinked' : ''}${tone ? ` result-${tone}` : ''}`}
                                selectable={false}
                                onClick={() => onOpenDoc && onOpenDoc(row.docId)}
                            >
                                <TableRowColumn>
                                    <span className="test-runs-mail-cell">
                                        <HoverTip label={row.subject}>
                                            <span>{row.docId.slice(0, 8)}</span>
                                        </HoverTip>
                                        <LetterLink sourceMailId={row.sourceMailId} />
                                    </span>
                                </TableRowColumn>
                                <TableRowColumn>{row.annotationType || '—'}</TableRowColumn>
                                <TableRowColumn>{row.linked ? row.presence : `${row.presence} (unlinked)`}</TableRowColumn>
                                {metricCells({
                                    fromMetrics: row.from,
                                    toMetrics: row.presence === 'removed' ? row.from : row.to,
                                    comparable: row.presence === 'both',
                                    percentMode,
                                })}
                            </TableRow>
                        );
                    })}
                    <TableRow>
                        <TableRowColumn>Shared totals</TableRowColumn>
                        <TableRowColumn>—</TableRowColumn>
                        <TableRowColumn>both</TableRowColumn>
                        {metricCells({
                            fromMetrics: sharedFrom,
                            toMetrics: sharedTo,
                            comparable: true,
                            percentMode,
                        })}
                    </TableRow>
                </TableBody>
            </Table>
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/CompareResults.jsx